import React, { useState } from 'react';
import { format } from 'date-fns';
import { useMyProvider } from '../../hooks/useMyProvider';
import { useAlerts } from '../../hooks/useAlerts';
import AlertBadge from '../../components/Dashboard/AlertBadge';
import { Severity } from '../../types/analytics';

const Alerts: React.FC = () => {
  const { provider, loading: providerLoading, error: providerError } = useMyProvider();
  const [days, setDays] = useState(7);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const { alerts, total, unacknowledgedCount, loading, acknowledgeAlert } = useAlerts(
    provider?.id || null,
    days,
    severityFilter || undefined
  );

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          {providerError || 'No provider profile found. Create your provider profile to see analytics.'}
        </p>
      </div>
    );
  }

  const filteredAlerts = showAcknowledged ? alerts : alerts.filter((a) => !a.acknowledged);

  const severityCounts = {
    critical: alerts.filter((a) => a.severity === 'critical').length,
    high: alerts.filter((a) => a.severity === 'high').length,
    medium: alerts.filter((a) => a.severity === 'medium').length,
    low: alerts.filter((a) => a.severity === 'low').length,
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-['Ovo'] text-2xl md:text-3xl text-[#1f1f1f] mb-2">Alerts</h1>
        <p className="font-['Open_Sans'] text-sm text-[#767472]">
          Monitor and manage important notifications about your reviews
        </p>
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="font-['Ovo'] text-2xl text-[#1f1f1f] mb-1">{total}</div>
          <div className="font-['Open_Sans'] text-xs text-[#767472] uppercase">Total</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="font-['Ovo'] text-2xl text-red-900 mb-1">{severityCounts.critical}</div>
          <div className="font-['Open_Sans'] text-xs text-red-700 uppercase">Critical</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <div className="font-['Ovo'] text-2xl text-orange-900 mb-1">{severityCounts.high}</div>
          <div className="font-['Open_Sans'] text-xs text-orange-700 uppercase">High</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <div className="font-['Ovo'] text-2xl text-yellow-900 mb-1">{severityCounts.medium}</div>
          <div className="font-['Open_Sans'] text-xs text-yellow-700 uppercase">Medium</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="font-['Ovo'] text-2xl text-gray-900 mb-1">{severityCounts.low}</div>
          <div className="font-['Open_Sans'] text-xs text-gray-700 uppercase">Low</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f] uppercase mb-4">
          Filters
        </h2>
        <div className="flex flex-wrap gap-4">
          {/* Days Filter */}
          <div>
            <label className="block font-['Open_Sans'] text-xs text-[#767472] uppercase mb-2">
              Time Period
            </label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-2 font-['Open_Sans'] text-sm bg-white border border-[#eaeaea] rounded focus:outline-none focus:border-[#c6a664]"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {/* Severity Filter */}
          <div>
            <label className="block font-['Open_Sans'] text-xs text-[#767472] uppercase mb-2">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-3 py-2 font-['Open_Sans'] text-sm bg-white border border-[#eaeaea] rounded focus:outline-none focus:border-[#c6a664]"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Acknowledged Filter */}
          <div>
            <label className="block font-['Open_Sans'] text-xs text-[#767472] uppercase mb-2">
              Status
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAcknowledged}
                onChange={(e) => setShowAcknowledged(e.target.checked)}
                className="w-4 h-4 text-[#c6a664] border-gray-300 rounded focus:ring-[#c6a664]"
              />
              <span className="font-['Open_Sans'] text-sm text-[#1f1f1f]">Show acknowledged</span>
            </label>
          </div>

          {/* Unacknowledged Count */}
          {unacknowledgedCount > 0 && (
            <div className="ml-auto flex items-end">
              <div className="px-4 py-2 bg-orange-50 border border-orange-200 rounded">
                <span className="font-['Open_Sans'] text-sm text-orange-800">
                  <strong>{unacknowledgedCount}</strong> unacknowledged alert
                  {unacknowledgedCount > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
          </div>
        ) : filteredAlerts.length > 0 ? (
          filteredAlerts.map((alert) => (
            <div
              key={alert.alert_id}
              className={`bg-white rounded-lg shadow-sm p-6 transition-all hover:shadow-md ${
                alert.acknowledged ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <AlertBadge severity={alert.severity as Severity} withIcon />
                  <span className="font-['Open_Sans'] text-xs text-[#767472] uppercase">
                    {alert.alert_type}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="font-['Open_Sans'] text-xs text-[#767472]">
                    {format(new Date(alert.created_at), 'MMM dd, yyyy HH:mm')}
                  </span>
                  {alert.acknowledged && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-['Open_Sans'] font-medium rounded">
                      Acknowledged
                    </span>
                  )}
                </div>
              </div>

              <p className="font-['Open_Sans'] text-base text-[#1f1f1f] mb-4">{alert.message}</p>

              {/* Alert Data */}
              {alert.data && Object.keys(alert.data).length > 0 && (
                <div className="mb-4 p-4 bg-[#f8f6f2] rounded">
                  <h4 className="font-['Open_Sans'] text-xs font-semibold text-[#767472] uppercase mb-2">
                    Details
                  </h4>
                  <pre className="font-['Open_Sans'] text-xs text-[#1f1f1f] overflow-x-auto">
                    {JSON.stringify(alert.data, null, 2)}
                  </pre>
                </div>
              )}

              {/* Acknowledge Button */}
              {!alert.acknowledged && (
                <button
                  onClick={() => acknowledgeAlert(alert.alert_id)}
                  className="px-4 py-2 bg-[#1f1f1f] text-white font-['Open_Sans'] text-sm font-semibold uppercase rounded hover:bg-[#3f3f3f] transition-colors"
                >
                  Acknowledge
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="font-['Open_Sans'] text-lg text-[#767472] mb-2">No alerts found</p>
            <p className="font-['Open_Sans'] text-sm text-[#767472]">
              {showAcknowledged
                ? 'Try adjusting your filters'
                : 'All alerts have been acknowledged'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alerts;
